import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cenQtyMin from "@tCentro/Handlers/CenQtyMinHandler";
import cenQtyMulti from "@tCentro/Handlers/CenQtyMultiHandler";
import CENErrorIcon from "@tCentro/Icons/CENErrorIcon";
import ProductQuantityOrdered from "@tCentro/Overrides/Components/ProductQuantityOrdered";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState & CustomApplicationState, ownProps: HasProduct) => {
    const { productInfo, product } = ownProps;
    return {
        configurationSelection: state.pages.productDetails.configurationSelection,
        configurationCompleted: state.pages.productDetails.configurationCompleted,
        variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
        qtyOrdered: productInfo.qtyOrdered,
        isVariantParent: product.isVariantParent,
        mutipleSaleQty: product?.detail?.multipleSaleQty,
        roundingRule: product?.detail?.roundingRule,
        isMinErrorObj: state.t_components.isMinError,
        isMultiErrorObj: state.t_components.isMultiError,
    };
};
const mapDispatchToProps = {
    cenQtyMin,
    cenQtyMulti,
};

export interface NISProductDetailsQuantityOrderedStyles {
    quantityOrdered?: TextFieldProps;
    quantityOrderedWithError?: TextFieldProps;
    mainWrapper?: InjectableCss;
    quantityOrderedWrapper?: InjectableCss;
    addQTYBtn?: ButtonProps;
    subQTYBtn?: ButtonProps;
    qtyTitle?: TypographyPresentationProps;
    tooltipContainerStyles?: InjectableCss;
    errorWrapper?: InjectableCss;
    errorMsgStyle?: InjectableCss;
    errorIconStyles?: IconPresentationProps;
    errorMsgStyleTypo?: TypographyPresentationProps;
}

export const nisQuantityOrderedStyles: NISProductDetailsQuantityOrderedStyles = {
    errorMsgStyle: {
        css: css`
            display: flex;
            justify-content: center;
            gap: 4px;
            position: absolute;
            margin-top: 4px;
        `,
    },
    errorMsgStyleTypo: {
        color: "danger",
        css: css`
            font-weight: 400;
            font-size: 14px;
            line-height: 20px;
        `,
    },
    errorIconStyles: {
        size: 20,
        css: css`
            span {
                width: 20px;
                height: 20px;
            }
        `,
    },
    tooltipContainerStyles: {
        css: css`
            background-color: #262525;
            border-radius: 4px;
            width: max-content;
            border-radius: 4px;
            box-shadow: 0px 4px 8px 0px rgba(38, 37, 37, 0.24);
            position: absolute;
            top: -22px;
            right: 50px;
            z-index: 999;
            font-size: 12px;
            font-style: normal;
            font-weight: 600;
            line-height: 16px;
            padding: 8px 16px;
            color: #262525;
            @media (max-width: 1024px) {
                top: -42px;
                right: 0px;
            }
            &:after {
                content: "";
                border-left: 0px solid ${getProp("theme.typography.p.color")};
                border-right: 4px solid ${getProp("theme.typography.p.color")};
                position: absolute;
                z-index: 999;
                top: 29px;
                left: 42%;
                width: 0px;
                height: 16px;
                width: 0px;
                height: 0px;
                border-left: 10px solid transparent;
                border-right: 10px solid transparent;
                border-top: 16px solid ${getProp("theme.typography.p.color")};
            }
            @media (min-width: 1024px) and (max-width: 1280px) {
                top: -20px;
                left: -35px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        top: -20px;
                        left: -14px;
                    `,
                    css`
                        top: -20px;
                        left: -14px;
                    `,
                    css`
                        left: -34px;
                        top: -22px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                // margin-top: 10px;
                width: initial;
                flex-direction: row !important;
                align-items: center;

                label {
                    font-weight: ${TYPOGRAPHY.weight.mediumbold};
                    font-size: ${TYPOGRAPHY.size.base};
                    line-height: 24px;
                    width: 35% !important;
                }

                > div {
                    margin-top: 0 !important;

                    input {
                        padding: 12px 16px !important;
                        height: 48px !important;
                        width: 130px !important;
                        border: 1px solid #e0dfe0 !important;
                        text-align: center;
                        margin: 0px 16px 0px 8px;
                        border-radius: 16px !important;
                        &::-webkit-outer-spin-button,
                        &::-webkit-inner-spin-button {
                            -webkit-appearance: none !important;
                        }
                        font-family: Roboto;
                        font-size: 16px !important;
                        line-height: 24px !important;
                        font-weight: 400 !important;
                    }
                }

                @media (min-width: 992px) and (max-width: 1180px) {
                    > div {
                        input {
                            width: 150px !important;
                        }
                    }
                }

                @media (max-width: 991px) {
                    > div {
                        input {
                            width: 150px !important;
                        }
                    }
                }

                @media (min-width: 768px) and (max-width: 1280px) {
                    > div {
                        input {
                            width: 130px !important;
                        }
                    }
                }

                @media (max-width: 420px) {
                    > div {
                        input {
                            width: 130px !important;
                        }
                    }
                }
            `,
        },
    },
    quantityOrderedWithError: {
        cssOverrides: {
            formInputWrapper: css`
                input {
                    border: 2px solid ${COLORS.alertMsgRed} !important;
                }
            `,
        },
    },

    mainWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            position: relative;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: center;
                        margin-bottom: 26px !important;
                    `,
                    css`
                        justify-content: center;
                        margin-bottom: 26px !important;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    quantityOrderedWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            > [class*="FormFieldStyle-"] {
                label {
                    display: none;
                }
            }
        `,
    },
    addQTYBtn: {
        css: css`
            height: 16px;
            width: 16px;
            margin-top: -2px;
            min-height: auto !important;
            background-color: ${COLORS.white};
            padding: 0px;
            position: absolute;
            right: 30px;
            z-index: 1;
            border: none;
            & svg {
                color: #333333;
                width: 16px;
                height: 16px;
            }
            & span {
                width: 16px;
                height: 16px;
            }
            &:hover {
                background-color: ${COLORS.white};
                border: none;
            }
            &:focus {
                outline: none;
            }
            &:disabled,
            &:disabled:hover {
                background: transparent;
            }
            &:active {
                background: transparent;
            }
        `,
    },
    subQTYBtn: {
        css: css`
            height: 16px;
            width: 16px;
            margin-top: -2px;
            min-height: auto !important;
            background-color: transparent;
            padding: 0px;
            position: absolute;
            left: 23px;
            z-index: 1;
            border: none;
            & svg {
                color: #333333;
                width: 16px;
                height: 16px;
            }
            & span {
                width: 16px;
                height: 16px;
            }
            &:hover {
                background-color: transparent;
                border: none;
            }
            &:focus {
                outline: none;
            }
            &:disabled,
            &:disabled:hover {
                background-color: transparent;
            }
            &:active {
                background: transparent;
            }
        `,
    },
    qtyTitle: {
        css: css`
            color: #333333 !important;
            font-weight: 600;
            font-size: 18px;
            line-height: 18px;
            // margin-top: 10px;
            font-family: Roboto !important;
            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [null, null, null, null, null])}
        `,
    },
};

const styles = nisQuantityOrderedStyles;

const NISProductDetailsQuantityOrdered = ({
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    qtyOrdered,
    isVariantParent,
    mutipleSaleQty,
    roundingRule,
    isMinErrorObj,
    isMultiErrorObj,
    cenQtyMin,
    cenQtyMulti,
}: Props) => {
    const [btnQty, setBtnQty] = React.useState(
        mutipleSaleQty && mutipleSaleQty > 0 && roundingRule === "Multiple Only" ? mutipleSaleQty : qtyOrdered,
    );
    const isMinError = isMinErrorObj?.isMinError;
    const isMultiError = isMultiErrorObj?.isMultiError;
    React.useEffect(() => {
        cenQtyMin({ isMinError: false });
        cenQtyMulti({ isMultiError: false });
    }, []);
    const quantityOrderedFinalStyles =
        isMinError || isMultiError ? styles.quantityOrdered : styles.quantityOrderedWithError;
    const [showTooltip, setShowTooltip] = React.useState(false);
    const handleMouseEnter = () => {
        if (mutipleSaleQty && mutipleSaleQty > 0 && roundingRule === "Multiple Only" && qtyOrdered === mutipleSaleQty) {
            setShowTooltip(true);
        } else {
            setShowTooltip(false);
        }
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    const addQtyFn = () => {
        setBtnQty(
            qtyOrdered +
                (mutipleSaleQty && mutipleSaleQty > 0 && roundingRule === "Multiple Only" ? mutipleSaleQty : 1),
        );
    };

    const subQtyFn = () => {
        const qtyValue =
            qtyOrdered -
            (mutipleSaleQty && mutipleSaleQty > 0 && roundingRule === "Multiple Only" ? mutipleSaleQty : 1);
        if (qtyValue === 0 || Math.sign(qtyValue) === -1) {
            setBtnQty(0);
            return false;
        }
        setBtnQty(qtyValue);
    };
    return (
        <>
            <StyledWrapper {...styles.mainWrapper}>
                <Typography as="p" {...styles.qtyTitle}>
                    {"QTY: "}
                </Typography>
                <StyledWrapper {...styles.quantityOrderedWrapper}>
                    <Button
                        disabled={
                            !!(
                                (!variantSelectionCompleted && isVariantParent) ||
                                (mutipleSaleQty &&
                                    mutipleSaleQty > 0 &&
                                    roundingRule === "Multiple Only" &&
                                    qtyOrdered === mutipleSaleQty)
                            )
                        }
                        {...styles.subQTYBtn}
                        data-test-selector="subQTYBtn"
                        onClick={subQtyFn}
                        onMouseOver={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        <ButtonIcon src={Minus} />
                    </Button>
                    {showTooltip && (
                        <StyledWrapper {...styles.tooltipContainerStyles}>
                            <Typography
                                as="span"
                                css={css`
                                    color: ${getColor("info.contrast")} !important;
                                `}
                            >
                                {translate(`${mutipleSaleQty} unit minimum`)}
                            </Typography>
                        </StyledWrapper>
                    )}
                    <ProductQuantityOrdered
                        configurationSelection={configurationSelection}
                        configurationCompleted={configurationCompleted}
                        variantSelectionCompleted={variantSelectionCompleted}
                        extendedStyles={styles.quantityOrdered}
                        onBtnclickQtyValue={btnQty}
                        setBtnQty={setBtnQty}
                    />
                    <Button
                        disabled={!variantSelectionCompleted && isVariantParent}
                        {...styles.addQTYBtn}
                        data-test-selector="addQTYBtn"
                        onClick={addQtyFn}
                    >
                        <ButtonIcon src={Plus} />
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
            {isMultiError === true && (
                <StyledWrapper {...styles.errorMsgStyle}>
                    <Icon {...styles.errorIconStyles} src={CENErrorIcon} />
                    <Typography {...styles.errorMsgStyleTypo}>
                        {translate(`This product must be ordered in multiples of ${mutipleSaleQty}`)}
                    </Typography>
                </StyledWrapper>
            )}
            {isMinError === true && (
                <StyledWrapper {...styles.errorMsgStyle}>
                    <Icon {...styles.errorIconStyles} src={CENErrorIcon} />
                    <Typography {...styles.errorMsgStyleTypo}>
                        {translate(`This product has a minimum order of ${mutipleSaleQty} units`)}
                    </Typography>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(NISProductDetailsQuantityOrdered)),
    definition: {
        displayName: "NIS Quantity Ordered",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
